import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import {
  getClubDay,
  getClubSportName,
  getClubStartIso,
  getClubFinishIso,
  fmtDMY
} from 'Src/helpers/clubProgramme/clubProgramme';
import { programmeBookingFormatTimeRange } from 'Src/helpers/clubProgramme/clubProgrammeBooking';
import { getProgrammeClubDayShortLabel, getProgrammeClubGenderLabel } from '../helpers/clubProgrammeClubsContent';

type Props = {
  user: AppUser;
  programme: AfterSchoolClubProgramme;
  clubDetailsById: { [clubId: string]: any };
  status: string;
  canEdit: boolean;
};

export const ClubProgrammeClubsTab: React.FC<Props> = ({ user, programme, clubDetailsById, status, canEdit }) => {
  const programmeClubs = Array.isArray(programme.clubs) ? programme.clubs : [];
  const isCompleted = String(status || '').toUpperCase() === 'COMPLETED';
  const naming = resolveAgeNamingFromContext(user, programme);

  return (
    <section className="cpProgrammeClubsSummary">
      <div className="cpProgrammeClubsSummaryHeader">
        <div>
          <h2>Programme clubs</h2>
          <p>
            {programmeClubs.length} {programmeClubs.length === 1 ? 'club' : 'clubs'} added to this programme.
          </p>
        </div>
      </div>

      {!canEdit ? (
        <div className="cpProgrammeClubsNotice">
          <span aria-hidden="true">!</span>
          <p>
            {isCompleted
              ? 'This programme is completed. Clubs are shown for reference and cannot be changed.'
              : 'This programme is no longer in Draft. Its clubs cannot be changed.'}
          </p>
        </div>
      ) : null}

      {programmeClubs.length ? (
        <div className="cpProgrammeClubsList">
          {programmeClubs.map((programmeClub: any, index: number) => {
            const clubId = String(programmeClub?.clubId || '');
            const club = clubDetailsById[clubId] || programmeClub?.club || {};
            const name = String(club?.name || programmeClub?.clubName || `Club ${index + 1}`);
            const sport = getClubSportName(club) || 'Sport not set';
            const day = getProgrammeClubDayShortLabel(getClubDay(club)) || 'Day not set';
            const schedule = propz.get(club, ['schedule'], {}) || {};
            const time =
              programmeBookingFormatTimeRange(schedule) || String(schedule?.startTime || '') || 'Time not set';
            const start = fmtDMY(getClubStartIso(club));
            const finish = fmtDMY(getClubFinishIso(club));
            const dates = start && finish ? `${start} – ${finish}` : start || finish || 'Dates not set';
            const gender = getProgrammeClubGenderLabel(club) || 'All genders';
            const ages = formatAgesCompact(propz.get(club, ['ages'], []), naming) || 'All ages';
            const enabled = programmeClub?.isEnabled !== false;
            const hasCustomFields = Boolean(programmeClub?.fieldsOverride);

            return (
              <article
                key={String(programmeClub?._id || programmeClub?.id || clubId || index)}
                className="cpProgrammeClubCard"
              >
                <div className="cpProgrammeClubCardTop">
                  <span className="cpProgrammeClubIcon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5Z" />
                    </svg>
                  </span>
                  <div className="cpProgrammeClubIdentity">
                    <h3>{name}</h3>
                    <p>{sport}</p>
                  </div>
                  <span className={enabled ? 'cpProgrammeClubState isEnabled' : 'cpProgrammeClubState isDisabled'}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                <div className="cpProgrammeClubDetails">
                  <div>
                    <span>Schedule</span>
                    <strong>
                      {day} · {time}
                    </strong>
                  </div>
                  <div>
                    <span>Dates</span>
                    <strong>{dates}</strong>
                  </div>
                  <div>
                    <span>Audience</span>
                    <strong>
                      {gender} · {ages}
                    </strong>
                  </div>
                  <div>
                    <span>Booking form</span>
                    <strong>{hasCustomFields ? 'Custom fields' : 'Programme default'}</strong>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="cpProgrammeClubsEmpty">
          <strong>No clubs added yet</strong>
          <span>Add clubs from Manage clubs to make them available in this programme.</span>
        </div>
      )}
    </section>
  );
};

ClubProgrammeClubsTab.displayName = 'ClubProgrammeClubsTab';
